import { useCallback } from 'react'
import { useFormatters } from '../../../contexts/PreferencesContext'
import { formatCompactCurrencyNumber } from '../../../lib/utils'

/**
 * CR-7120: the tick formatter for a dashboard graph's money axis — numbers only.
 *
 * The axes used to read "£0, £1M, ..."; they now read "0, 1M, ...". The chart
 * title already says the axis is a value, and the stat cards and KPI tiles keep
 * their currency symbol. Only the AXES change: tooltips are untouched. Percent
 * axes keep their "%" and do not use this.
 *
 * The magnitude keeps the currency's own convention (INR groups in lakhs and
 * crores, "3.4Cr"; everything else in K/M), exactly as before minus the symbol.
 *
 * @param currency - Currency whose compact convention applies; defaults to the
 *   user's preferred currency.
 * @returns A `tickFormatter` for a recharts axis.
 *
 * @example
 * const axisAmount = useAxisAmount()
 * <YAxis tickFormatter={axisAmount} />
 */
export function useAxisAmount(currency?: string): (value: number) => string {
  const { preferences } = useFormatters()
  const code = currency || preferences?.currency
  return useCallback((value: number) => formatCompactCurrencyNumber(value, code), [code])
}
